import { useState } from 'react';
import { Copy, Check, Terminal, Apple, Monitor } from 'lucide-react';

/**
 * Was mit dem privaten Schlüssel zu tun ist — je Betriebssystem.
 *
 * ══ WARUM DAS HIERHIN GEHÖRT ═══════════════════════════════════════════════
 *
 * Der Schlüssel wird genau einmal angezeigt. Wer in diesem Moment erst
 * anfangen muss zu suchen, wo die Datei hingehört und welche Rechte sie
 * braucht, kopiert ihn in eine Textdatei auf dem Schreibtisch — und dort
 * bleibt er dann.
 *
 * ══ DIE RECHTE SIND DER HÄUFIGSTE FEHLER ═══════════════════════════════════
 *
 * OpenSSH weigert sich, einen privaten Schlüssel zu benutzen, den andere lesen
 * können. Die Meldung lautet „UNPROTECTED PRIVATE KEY FILE" und nennt zwar das
 * Problem, aber nicht die Lösung — und unter Windows, wo `chmod` nichts tut,
 * hilft sie gar nicht weiter. Deshalb steht bei jedem System der Befehl dabei,
 * der es richtigstellt.
 */

type System = 'mac' | 'linux' | 'windows';

function Befehl({ text }: { text: string }) {
  const [kopiert, setKopiert] = useState(false);
  return (
    <div className="group relative">
      <pre className="overflow-x-auto rounded bg-slate-900 px-3 py-2 pr-9 text-[11px] leading-relaxed text-slate-100">
        {text}
      </pre>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard.writeText(text);
          setKopiert(true);
          window.setTimeout(() => setKopiert(false), 1500);
        }}
        title="kopieren"
        className="absolute right-1.5 top-1.5 rounded p-1 text-slate-400 hover:bg-slate-700 hover:text-slate-100"
      >
        {kopiert ? <Check size={11} /> : <Copy size={11} />}
      </button>
    </div>
  );
}

export default function SchluesselAnleitung({ dateiname = 'id_ed25519' }: { dateiname?: string }) {
  const [system, setSystem] = useState<System>('mac');

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-1.5">
        {(
          [
            ['mac', 'macOS', Apple],
            ['linux', 'Linux', Terminal],
            ['windows', 'Windows', Monitor],
          ] as Array<[System, string, typeof Apple]>
        ).map(([wert, text, Symbol]) => (
          <button
            key={wert}
            type="button"
            onClick={() => setSystem(wert)}
            className={`inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium ${
              system === wert
                ? 'bg-brand-600 text-white'
                : 'border border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <Symbol size={11} /> {text}
          </button>
        ))}
      </div>

      <div className="mt-2.5 space-y-2.5 text-[11px] text-slate-700">
        {system !== 'windows' ? (
          <>
            <div>
              <div className="mb-1 font-medium">1. Datei anlegen und einfügen</div>
              <Befehl
                text={`mkdir -p ~/.ssh && chmod 700 ~/.ssh\nnano ~/.ssh/${dateiname}`}
              />
              <p className="mt-1 text-slate-500">
                Den kopierten Schlüssel einfügen, mit <kbd>Strg</kbd>+<kbd>O</kbd> speichern,{' '}
                <kbd>Strg</kbd>+<kbd>X</kbd> schliessen. Die Datei muss mit{' '}
                <code>-----BEGIN</code> anfangen und mit <code>-----END…-----</code> aufhören, und
                die letzte Zeile braucht einen Zeilenumbruch.
              </p>
            </div>

            <div>
              <div className="mb-1 font-medium">2. Rechte setzen — ohne das geht es nicht</div>
              <Befehl text={`chmod 600 ~/.ssh/${dateiname}`} />
              <p className="mt-1 text-slate-500">
                Fehlt dieser Schritt, verweigert OpenSSH den Dienst mit{' '}
                <em>„UNPROTECTED PRIVATE KEY FILE"</em>. Das ist keine Warnung, sondern ein Abbruch.
              </p>
            </div>

            <div>
              <div className="mb-1 font-medium">3. Verbinden</div>
              <Befehl text={`ssh -i ~/.ssh/${dateiname} root@<server-ip>`} />
              <p className="mt-1 text-slate-500">
                Damit der Schlüssel von selbst genommen wird, einen Eintrag in{' '}
                <code>~/.ssh/config</code> anlegen:
              </p>
              <div className="mt-1">
                <Befehl
                  text={`Host juni-*\n  User root\n  IdentityFile ~/.ssh/${dateiname}\n  IdentitiesOnly yes`}
                />
              </div>
              <p className="mt-1 text-slate-500">
                <code>IdentitiesOnly yes</code> ist nicht optional, wenn mehrere Schlüssel im
                Agenten liegen: OpenSSH probiert sonst alle durch, und der Server bricht nach
                sechs Fehlversuchen ab — mit <em>„Too many authentication failures"</em>, was wie
                ein falscher Schlüssel aussieht.
              </p>
            </div>

            {system === 'mac' && (
              <div>
                <div className="mb-1 font-medium">4. Nur macOS: in den Schlüsselbund</div>
                <Befehl text={`ssh-add --apple-use-keychain ~/.ssh/${dateiname}`} />
                <p className="mt-1 text-slate-500">
                  Damit fragt macOS die Passphrase nur einmal. Zusätzlich in{' '}
                  <code>~/.ssh/config</code> unter den Host-Eintrag:{' '}
                  <code>UseKeychain yes</code> und <code>AddKeysToAgent yes</code>.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <div>
              <div className="mb-1 font-medium">1. Datei anlegen und einfügen</div>
              <p className="mb-1 text-slate-500">
                In PowerShell — der Ordner <code>.ssh</code> fehlt oft noch:
              </p>
              <Befehl
                text={`mkdir "$env:USERPROFILE\\.ssh" -Force\nnotepad "$env:USERPROFILE\\.ssh\\${dateiname}"`}
              />
              <p className="mt-1 text-slate-500">
                Notepad fragt beim Speichern nach dem Dateityp — <strong>Alle Dateien</strong>{' '}
                wählen, sonst wird daraus <code>{dateiname}.txt</code> und nichts funktioniert.
              </p>
            </div>

            <div>
              <div className="mb-1 font-medium">2. Rechte setzen — ohne das geht es nicht</div>
              <p className="mb-1 text-slate-500">
                <code>chmod</code> gibt es hier nicht; Windows regelt das über Zugriffslisten.
                Dieser Befehl nimmt alle Erben heraus und lässt nur dich übrig:
              </p>
              <Befehl
                text={`icacls "$env:USERPROFILE\\.ssh\\${dateiname}" /inheritance:r /grant:r "$env:USERNAME:R"`}
              />
              <p className="mt-1 text-slate-500">
                Ohne das meldet OpenSSH <em>„Permissions for … are too open"</em> und verweigert
                den Dienst — derselbe Abbruch wie unter macOS, nur mit anderem Wortlaut.
              </p>
            </div>

            <div>
              <div className="mb-1 font-medium">3. Verbinden</div>
              <Befehl
                text={`ssh -i "$env:USERPROFILE\\.ssh\\${dateiname}" root@<server-ip>`}
              />
              <p className="mt-1 text-slate-500">
                Windows 10 und 11 bringen OpenSSH mit. Fehlt der Befehl:{' '}
                <em>Einstellungen → Apps → Optionale Features → OpenSSH-Client</em>.
              </p>
            </div>

            <div>
              <div className="mb-1 font-medium">4. Dauerhaft: die config-Datei</div>
              <Befehl
                text={`notepad "$env:USERPROFILE\\.ssh\\config"`}
              />
              <div className="mt-1">
                <Befehl
                  text={`Host juni-*\n  User root\n  IdentityFile ~/.ssh/${dateiname}\n  IdentitiesOnly yes`}
                />
              </div>
              <p className="mt-1 text-slate-500">
                In der config-Datei funktioniert <code>~</code> auch unter Windows — anders als in
                PowerShell selbst.
              </p>
            </div>

            <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-amber-800">
              <strong>PuTTY?</strong> Das kann mit diesem Format nichts anfangen. Den Schlüssel in
              PuTTYgen über <em>Conversions → Import key</em> laden und als <code>.ppk</code>{' '}
              speichern. Wer die Wahl hat, nimmt den OpenSSH-Client — er ist ohnehin da.
            </div>
          </>
        )}

        <div className="rounded border border-slate-200 bg-white px-2 py-1.5 text-slate-600">
          <strong>Kommt trotzdem „Permission denied"?</strong> Dann ist der Schlüssel noch nicht
          auf dem Server. Er wird beim nächsten Abgleich verteilt — ein Admin stösst ihn an, oder
          er läuft beim nächsten Ausrollen mit.
        </div>
      </div>
    </div>
  );
}
